import type {
  MetadataCollectionOptions,
  MetadataCollectionResult,
  MetadataCollectionSummary,
  CompleteMetadata,
  GitMetadata,
  CIMetadata,
  SystemMetadata,
  PlaywrightMetadata,
} from './types.js';
import { GitMetadataCollector } from './git.js';
import { CIMetadataCollector } from './ci.js';
import { SystemMetadataCollector } from './system.js';
import { PlaywrightMetadataCollector } from './playwright.js';
import { withTimeout as withTimeoutUtil } from '../utils/index.js';
import type { PlaywrightFullConfig, PlaywrightSuite } from './playwright.js';

const DEFAULT_METADATA_OPTIONS: Required<MetadataCollectionOptions> = {
  timeout: 5000,
  debug: false,
};

export class MetadataAggregator {
  private options: Required<MetadataCollectionOptions>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private collectors: Array<{ collectWithResult: () => Promise<MetadataCollectionResult<any>> }> = [];
  private playwrightCollector?: PlaywrightMetadataCollector;

  constructor(options: MetadataCollectionOptions = {}) {
    this.options = { ...DEFAULT_METADATA_OPTIONS, ...options };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerCollector(collector: { collectWithResult: () => Promise<MetadataCollectionResult<any>> }): void {
    this.collectors.push(collector);
    if (collector instanceof PlaywrightMetadataCollector) {
      this.playwrightCollector = collector;
    }
  }

  buildSkeleton(suite: PlaywrightSuite): CompleteMetadata['skeleton'] {
    if (!this.playwrightCollector) return undefined;
    return this.playwrightCollector.buildSkeleton(suite);
  }

  async collectAll(): Promise<MetadataCollectionSummary> {
    const startTime = Date.now();

    const settledResults = await Promise.allSettled(
      this.collectors.map((collector) =>
        this.withTimeout(collector.collectWithResult(), this.options.timeout, 'Metadata collection')
      )
    );

    const results: MetadataCollectionResult[] = [];
    const metadata: CompleteMetadata = {};

    for (const settledResult of settledResults) {
      if (settledResult.status === 'fulfilled') {
        const result = settledResult.value;
        results.push(result);
        this.aggregateMetadata(metadata, result);
      } else {
        const error = settledResult.reason;
        console.warn('⚠️  TestDino: Metadata collector promise rejected:', error);
        results.push({
          data: {},
          success: false,
          error: error instanceof Error ? error.message : String(error),
          duration: 0,
          collector: 'unknown',
        });
      }
    }

    // Cross-collector reconciliation: needs both git + ci results. Bitbucket
    // post-merge runs are reclassified from push → pull_request here.
    if (metadata.ci && metadata.git) {
      CIMetadataCollector.reconcileWithGit(metadata.ci, metadata.git);
      CIMetadataCollector.resolveActorIsAuthor(metadata.ci, metadata.git);
    }
    delete metadata.git?.commit?.verifiedLogin;

    const totalDuration = Date.now() - startTime;
    const successCount = results.filter((r) => r.success).length;
    return {
      metadata,
      results,
      totalDuration,
      successCount,
      failureCount: results.length - successCount,
    };
  }

  private aggregateMetadata(metadata: CompleteMetadata, result: MetadataCollectionResult): void {
    const { collector, data } = result;
    switch (collector) {
      case 'git':
        metadata.git = data as GitMetadata;
        break;
      case 'ci':
        metadata.ci = data as CIMetadata;
        break;
      case 'system':
        metadata.system = data as SystemMetadata;
        break;
      case 'playwright':
        metadata.playwright = data as PlaywrightMetadata;
        break;
      default:
        if (this.options.debug) {
          console.warn(`⚠️  TestDino: Unknown metadata collector: ${collector}`);
        }
    }
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, operation: string): Promise<T> {
    return withTimeoutUtil(promise, timeoutMs, operation);
  }
}

export type {
  MetadataCollectionOptions,
  MetadataCollectionResult,
  MetadataCollectionSummary,
  CompleteMetadata,
  GitMetadata,
  CIMetadata,
  SystemMetadata,
  PlaywrightMetadata,
  PRMetadata,
  ShardMetadata,
  MetadataCollector,
  RunSkeleton,
  SkeletonSuite,
  SkeletonTest,
} from './types.js';

export { BaseMetadataCollector } from './base.js';
export { GitMetadataCollector } from './git.js';
export { CIMetadataCollector } from './ci.js';
export { SystemMetadataCollector } from './system.js';
export { PlaywrightMetadataCollector } from './playwright.js';

export function createMetadataCollector(
  playwrightConfig?: PlaywrightFullConfig,
  playwrightSuite?: PlaywrightSuite
): MetadataAggregator {
  const aggregator = new MetadataAggregator();
  aggregator.registerCollector(new GitMetadataCollector());
  aggregator.registerCollector(new CIMetadataCollector());
  aggregator.registerCollector(new SystemMetadataCollector());
  aggregator.registerCollector(
    new PlaywrightMetadataCollector({
      config: playwrightConfig,
      suite: playwrightSuite,
    })
  );
  return aggregator;
}
